import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import SidebarIcon, { SidebarIconsSrc } from "components/sidebar-icon/sidebar-icon";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { UserRoleValue } from "constants/field-values";
import routeNames from "constants/route-names";

const featureFlagIntelligenceEnabled = process.env.REACT_APP_FF_INTELLIGENCE_ENABLED == "true" || false;

const productCardValues = {
	route: routeNames.ADMIN_DEVICES,
	icon: SidebarIconsSrc.DEVICES,
	title: "Devices",
	subtitle: "Manage the equipment and systems that you offer."
};

const propertyCardValues = {
	route: routeNames.ADMIN_PROPERTIES,
	icon: SidebarIconsSrc.PROPERTIES,
	title: "Properties",
	subtitle: "Manage your customers’ properties, including products installed."
};

const userCardValues = {
	route: routeNames.ADMIN_USERS,
	icon: SidebarIconsSrc.USERS,
	title: "Users",
	subtitle: "Manage or invite new users."
};

const organizationCardValues = {
	route: routeNames.ADMIN_ORGANIZATIONS,
	icon: SidebarIconsSrc.ORGANIZATIONS,
	title: "Organizations",
	subtitle: "Manage your customer organizations."
};

const intelligenceCardValues = {
	route: routeNames.ADMIN_INTELLIGENCE,
	icon: SidebarIconsSrc.INTELLIGENCE,
	title: "Intelligence",
	subtitle:
		"Chat with the AI Assistant to identify strengths and gaps in your team's knowledge, based on their use of the platform."
};

const AdminDashboard: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const navigate = useNavigate();

	const dashboardCards = useMemo(() => {
		const cards: any = [];

		switch (loggedUserValue.role) {
			case UserRoleValue.admin_user:
			case UserRoleValue.vendor_manager:
				cards.push(productCardValues, propertyCardValues, userCardValues, organizationCardValues);
				if (featureFlagIntelligenceEnabled) {
					cards.push(intelligenceCardValues);
				}
				break;

			case UserRoleValue.vendor_employee:
				cards.push(productCardValues, propertyCardValues);
				break;

			case UserRoleValue.customer_manager:
				cards.push(
					{
						...propertyCardValues,
						subtitle:
							"Manage your organization's properties, including contact information, resources, and your staff's property-level access."
					},
					{
						...userCardValues,
						subtitle: "Manage or invite new users, including their property-level access."
					},
					{
						...organizationCardValues,
						subtitle: "Manage or create new vendor organizations."
					}
				);

				if (featureFlagIntelligenceEnabled) {
					cards.push(intelligenceCardValues);
				}
				break;
			default:
				break;
		}

		return (
			<div className="dashboard-cards-container">
				{cards.map((card, index) => {
					return (
						<div
							key={`admin-dashboard-card-${index}-${card.title}`}
							className="admin-dashboard-card"
							onClick={() => navigate(card.route)}
						>
							<SidebarIcon icon={card.icon} isSelected={true} />
							<span className="admin-dashboard-card-title">{card.title}</span>
							<p className="admin-dashboard-card-subtitle">{card.subtitle}</p>
						</div>
					);
				})}
			</div>
		);
	}, [loggedUserValue.role]);

	return (
		<div className="admin-dashboard-container">
			<div className="admin-dashboard-content-container">
				<div className="admin-dashboard-title-container">
					<span className="admin-dashboard-title">{`Hi ${loggedUserValue.first_name}`}</span>
					<span className="admin-dashboard-card-subtitle">{"What would you like to do?"}</span>
				</div>
				{dashboardCards}
			</div>
		</div>
	);
};

export default AdminDashboard;
